"use client";

import {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

import useHash from "@/hooks/use_hash";
import { FetchedAggregateCoherenceLink } from "@/types/coherence";
import { KeyFactor } from "@/types/comment";

type KeyFactorOverlayState =
  | { kind: "keyFactor"; keyFactor: KeyFactor }
  | { kind: "questionLink"; link: FetchedAggregateCoherenceLink }
  | null;

type QuestionLayoutContextValue = {
  // Key Factors Section UI State
  keyFactorsExpanded?: boolean;
  requestKeyFactorsExpand: () => void;

  // Key Factor Overlay
  keyFactorOverlay: KeyFactorOverlayState;
  openKeyFactorOverlay: (kf: KeyFactor) => void;
  openQuestionLinkOverlay: (link: FetchedAggregateCoherenceLink) => void;
  closeKeyFactorOverlay: () => void;

  // Comment reply trigger
  replyToCommentId: number | null;
  requestReplyToComment: (commentId: number) => void;
  clearReplyToComment: () => void;

  // Mobile tab state
  mobileActiveTab?: string;
  setMobileActiveTab: (tab: string) => void;
};

const QuestionLayoutContext = createContext({} as QuestionLayoutContextValue);

export const QuestionLayoutProvider = ({ children }: PropsWithChildren) => {
  const hash = useHash();
  const [keyFactorsExpanded, setKeyFactorsExpanded] = useState<boolean>();
  const [mobileActiveTab, setMobileActiveTab] = useState<string>();
  const [keyFactorOverlay, setKeyFactorOverlay] =
    useState<KeyFactorOverlayState>(null);

  // Expand key factors section if URL hash points to it
  useEffect(() => {
    if (hash === "key-factors") {
      setKeyFactorsExpanded(true);
      setMobileActiveTab("key-factors");
    }
  }, [hash]);

  const requestKeyFactorsExpand = useCallback(() => {
    setKeyFactorsExpanded(true);
    setMobileActiveTab("key-factors");
  }, []);

  const openKeyFactorOverlay = useCallback((kf: KeyFactor) => {
    setKeyFactorOverlay({ kind: "keyFactor", keyFactor: kf });
  }, []);

  const openQuestionLinkOverlay = useCallback(
    (link: FetchedAggregateCoherenceLink) => {
      setKeyFactorOverlay({ kind: "questionLink", link });
    },
    []
  );

  const closeKeyFactorOverlay = useCallback(() => {
    setKeyFactorOverlay(null);
  }, []);

  const [replyToCommentId, setReplyToCommentId] = useState<number | null>(null);
  const requestReplyToComment = useCallback((commentId: number) => {
    setReplyToCommentId(commentId);
  }, []);
  const clearReplyToComment = useCallback(() => {
    setReplyToCommentId(null);
  }, []);

  const value = useMemo<QuestionLayoutContextValue>(
    () => ({
      keyFactorsExpanded,
      requestKeyFactorsExpand,
      keyFactorOverlay,
      openKeyFactorOverlay,
      openQuestionLinkOverlay,
      closeKeyFactorOverlay,
      replyToCommentId,
      requestReplyToComment,
      clearReplyToComment,
      mobileActiveTab,
      setMobileActiveTab,
    }),
    [
      keyFactorsExpanded,
      requestKeyFactorsExpand,
      keyFactorOverlay,
      openKeyFactorOverlay,
      openQuestionLinkOverlay,
      closeKeyFactorOverlay,
      replyToCommentId,
      requestReplyToComment,
      clearReplyToComment,
      mobileActiveTab,
    ]
  );

  return (
    <QuestionLayoutContext.Provider value={value}>
      {children}
    </QuestionLayoutContext.Provider>
  );
};

export const useQuestionLayout = () => {
  const ctx = useContext(QuestionLayoutContext);
  if (!ctx) {
    throw new Error(
      "useQuestionLayout must be used within QuestionLayoutProvider"
    );
  }
  return ctx;
};

/**
 * Safe version that returns null instead of throwing if provider isn't available.
 * Use in components that might render outside of QuestionLayout (e.g., prediction flow).
 */
export const useQuestionLayoutSafe = () => {
  return useContext(QuestionLayoutContext);
};
